import type { TFunction } from 'i18next'
import type { RelatedLink } from './related-links'

/**
 * Canonical catalogue of marketing pages. A single source of truth for the
 * cross-link graph (pillar ↔ compare ↔ use-case) so every page links to the
 * others consistently. Titles/descriptions resolve from the page's own i18n
 * `marketing.<key>.link.*` keys, keeping copy bilingual in one place.
 */
export const MARKETING_PAGES = [
  { key: 'whatIsKnowledgeGraph', to: '/what-is-a-knowledge-graph', crumb: 'What is a knowledge graph?' },
  { key: 'aiKnowledgeGraphGenerator', to: '/ai-knowledge-graph-generator', crumb: 'AI knowledge graph generator' },
  { key: 'deepResearchAiTool', to: '/deep-research-ai-tool', crumb: 'Deep research AI tool' },
  { key: 'visualResearchTool', to: '/visual-research-tool', crumb: 'Visual research tool' },
  { key: 'comparePerplexity', to: '/compare/perplexity', crumb: 'MindWeb vs Perplexity' },
  { key: 'compareNotebooklm', to: '/compare/notebooklm', crumb: 'MindWeb vs NotebookLM' },
  { key: 'compareConnectedPapers', to: '/compare/connected-papers', crumb: 'MindWeb vs Connected Papers' },
  { key: 'compareGenspark', to: '/compare/genspark', crumb: 'MindWeb vs Genspark & Felo' },
  { key: 'compareObsidian', to: '/compare/obsidian', crumb: 'MindWeb vs Obsidian' },
  { key: 'useCasesResearchers', to: '/use-cases/researchers', crumb: 'Researchers & analysts' },
  { key: 'useCasesStudents', to: '/use-cases/students', crumb: 'Students' },
  { key: 'useCasesFounders', to: '/use-cases/founders', crumb: 'Founders & builders' },
  { key: 'useCasesKnowledgeWorkers', to: '/use-cases/knowledge-workers', crumb: 'Knowledge workers' },
] as const

export type MarketingPageKey = (typeof MARKETING_PAGES)[number]['key']

/** Look up a page's catalogue entry (path + breadcrumb label) by its i18n key. */
export function marketingPage(key: MarketingPageKey) {
  return MARKETING_PAGES.find((p) => p.key === key)!
}

/**
 * Build the related-link cards for a page, excluding the page itself, in the
 * order given by `keys`. Each card's copy comes from the target page's link.*
 * i18n keys so wording stays consistent across the site.
 */
export function buildRelatedLinks(
  t: TFunction,
  currentKey: MarketingPageKey,
  keys: readonly MarketingPageKey[],
): readonly RelatedLink[] {
  return keys
    .filter((key) => key !== currentKey)
    .map((key) => {
      const page = marketingPage(key)
      return {
        to: page.to,
        title: t(`marketing.${key}.link.title`),
        description: t(`marketing.${key}.link.description`),
      }
    })
}
